import $ from 'jquery';
import 'jquery.hotkeys';
import {callLater, cardTextOnly, checkWrittenAnswer, fitText, isEnterEvent, noop, shuffleArray} from "./playUtil.es6";
import {arrayRandomItem} from "./arrayUtil.es6";
import stopEvent from "./stopEvent.es6";
import Texter from "./texter.es6";
import PackLearnProgress from "./packLearnProgress.es6";
import PackReviewProgress from "./packReviewProgress.es6";
import {PackLearnQueue} from "./packLearnQueue.es6";
import PlayAudio from "./memo/playAudio.es6";
import PackLearnConfig from "./packLearnConfig.es6";
import PackReviewConfig from "./packReviewConfig.es6";
import RandomArrayAdapter from "./randomArrayAdapter.es6";
import WaitUserAction from "./waitUserAction.es6";
import CardSound from "./cardSound.es6";
// import "./hideAddressBar.es6";

require('jsrender')($);

// const log = console.log;
const log = noop;

const QUESTION_TEMPLATE = $.templates(`
<div class="play-card-stack__card">
    <div class="play-card-stack__card-side {{if image && text}}play-card-stack__card-side--image-and-text{{/if}} play-card-stack__card-face" data-lang="{{attr:lang}}">
        {{if audio}}<audio src="{{attr:audio}}"></audio>{{/if}}
        {{if image}}<div class="play-card-stack__card-image"><img alt="" src="{{attr:image}}" data-fitimage style="display: none;"></div>{{/if}}
        {{if text}}<div class="play-card-stack__card-text"><div class="fl-tt-{{: textType}}" data-fittext>{{if textType == 'html}}{{: text}}{{else}}{{> text}}{{/if}}</div></div>{{/if}}
    </div>
    <div class="fl-memo-card__tools"><button type="button" class="btn btn-light fl-memo-card__audio-btn" data-click="playAudio"><span class="fal fa-volume"></span></button></div>
</div>
`);

const WRONG_TEMPLATE = $.templates(`
<div class="play-card-stack__card">
    <div class="play-card-stack__card-side">
        <div class="fl-learn-card-wrong-display">
            <div class="fl-learn-card-wrong-display__title">
                <span class="far fa-frown"></span>
                <span>{{> title}}</span>
            </div>
            <div class="fl-learn-card-wrong-display__value">
                <span class="fl-learn-card-wrong-display__value-label fl-keep-nl">{{> label}}</span>
                <div class="fl-learn-card-wrong-display__value-answer"><div class="fl-tt-{{:answerTextType}}" data-fittext>{{if answerTextType == 'html}}{{: answerText}}{{else}}{{> answerText}}{{/if}}</div></div>
            </div>
            <div class="fl-learn-card-wrong-display__continue">{{> continueText}}</div>
        </div>
    </div>
</div>
`);

const REVIEW_COMPLETE = $.templates(`
<div class="play-card-stack__card">
    <div class="play-card-stack__card-side">
        <div class="play-card-stack__card-text">
            <p style="font-size: 2rem;">{{>~l18n.get('learnPage.reviewComplete.title')}}</p>
            <p style="font-size: 1rem;">{{>~l18n.get('learnPage.reviewComplete.text')}}</p>
        </div>
    </div>
</div>
`);

class PackLearn {
    constructor(opts) {
        // workaround for empty parameters

        opts.cards = opts.cards || [];
        opts.cardsForChoice = opts.cardsForChoice || [];

        // real init starts here

        this.l18n = Texter.create(opts.l18n);
        this.packId = opts.packId;
        this.cardView = opts.cardView;
        this.answerList = opts.answerList;
        this.answerWrite = opts.answerWrite;
        this.authenticated = opts.authenticated;

        this.reviewMode = !!opts.reviewMode;
        this.reviewConfig = opts.reviewConfig;

        // setup learn config
        if (opts.learnConfig != null) {
            this.progress = new PackLearnProgress(opts.progressView, opts.progress || {});
            this.config = new PackLearnConfig({
                l18n: this.l18n,
                packId: this.packId,
                authenticated: this.authenticated,
                config: opts.learnConfig,
                onSave: () => {
                    this._restartCurrent();
                },
                onReset: () => {
                    this.progress.clear();
                    this._saveStatus();
                }
            });

            CardSound.addReadyCallback(() => {
                this.cardView.addClass('play-card-stack--speakable');
            });
        }

        // setup review config
        if (opts.reviewConfig != null) {
            this.progress = new PackReviewProgress(opts.progressView, opts.progress || {});
            this.config = new PackReviewConfig({
                l18n: this.l18n,
                packId: this.packId,
                authenticated: this.authenticated,
                config: opts.reviewConfig,
                onSave: () => {
                    this._restartCurrent();
                },
                onReset: () => {
                    this.progress.clear();
                    this._saveStatus();
                }
            });
        }

        this.learnQueue = new PackLearnQueue({
            progress: this.progress,
            dropMastered: opts.reviewConfig != null,
            fullRandom: opts.learnConfig && opts.learnConfig.rand
        });

        this.empty = !(opts.cards.length > 0);

        // fill task cards
        this.cards = {};
        opts.cards.forEach((item) => this.cards[item.id] = item);

        // fill known cards (used for choice)
        this.cardsEverMeet = {};
        opts.cards.forEach((item) => this.cardsEverMeet[item.id] = item);
        opts.cardsForChoice.forEach((item) => this.cardsEverMeet[item.id] = item);

        // setup known cards random
        this.randomCards = new RandomArrayAdapter();
        this.randomCards.addAll(opts.cards.map(item => item.id));
        this.randomCards.addAll(opts.cardsForChoice.map(item => item.id));

        this.progress.validate(this.cards);
        this.learnQueue.init();

        // bind tune config button
        opts.tunePlay.click(e => {
            this.config.show();
            return stopEvent(e);
        });

        this.card = null;
        /**
         * @type {PackLearnStrategy}
         */
        this.cardStrategy = null;
        this.cardConfig = null;

        this.userChoiceEnabled = false;

        this.updateProgress();
    }

    get userChoiceEnabled() {
        return this._userChoiceEnabled;
    }

    set userChoiceEnabled(v) {
        this._userChoiceEnabled = v;
        this.answerList.find('a').prop('disabled', !v).toggleClass('disabled', !v);
    }

    updateProgress() {
        this.progress.ignoreSelectForStatus = !this.config.mSelect;
        this.progress.ignoreWriteForStatus = !this.config.mWrite;
        this.progress.updateView();
    }

    /**
     * Возвращает рандомный список карт
     *
     * @param count вот столько карт должно быть в наборе
     * @param excludeCardId эта карта не должна попасть в набор
     */
    randomCardsWithout(count, excludeCardId) {
        if (!excludeCardId) {
            excludeCardId = 0;
        }

        const out = [];
        for (let i = 0; out.length < count && i < this.randomCards.size; ++i) {
            const next = this.randomCards.next();
            if (next === excludeCardId) {
                continue;
            }
            out.push(next);
        }

        return out;
    }

    start() {
        this._next();
        this.updateProgress();
    }

    _restartCurrent() {
        this._next(true);
        this.updateProgress();
    }

    /**
     *
     * @param {boolean} [scrollTo] if you need scrolling to question
     * @private
     */
    _next(scrollTo) {
        if (this.cardStrategy != null) {
            this.cardStrategy.stop();
            this.cardStrategy = null;
        }

        this.answerList.hide();
        this.answerWrite.hide();

        // в ревью режиме, если все в mastered, то пишем поздравление, и все
        if (this.reviewMode && this.progress.isAllMastered()) {
            this.showReviewComplete();
            return;
        }

        const card = this.card = this.learnQueue.current();
        this.cardConfig = this._selectQuestionConfig();
        this.cardStrategy = this._selectQuestionStrategy(card);

        const questionModel = this._prepareQuestionModel(card);
        const questionHtml = QUESTION_TEMPLATE.render(questionModel);
        this.cardView.empty().append(questionHtml);

        PlayAudio.initSingleSide(this.cardView.find('button[data-click=playAudio]'), this.config.playAudio,
            this.cardView.find('.play-card-stack__card-side'));

        fitText(this.cardView.find('p[data-fittext], div[data-fittext]'));
        this.cardView.find('img[data-fitimage]')
            .fitImage({show: true});

        this.cardStrategy.show(this);

        if (scrollTo) {
            this._scrollToCard();
        }

        callLater(() => {
            this.userChoiceEnabled = true;
        });
    }

    showReviewComplete() {
        this.cardView.empty().append(REVIEW_COMPLETE.render({}, this));
    }

    /***
     * @private
     * @param card
     * @returns {PackLearnStrategy}
     */
    _selectQuestionStrategy(card) {
        const candidates = [];
        if (this.config.mSelect) {
            candidates.push(new PackLearnChoice(this));
        }

        if (this.config.mWrite) {
            if (this.progress.getCorrect('select', card) >= 2 || candidates.length === 0) {
                candidates.push(new PackLearnWrite(this));
            }
        }

        return arrayRandomItem(candidates);
    }

    _selectQuestionConfig() {
        const candidates = [];
        if (this.config.qTerm) candidates.push('term');
        if (this.config.qDefinition) candidates.push('definition');
        return {
            question: arrayRandomItem(candidates)
        };
    }

    _scrollToCard() {
        // да не надо уже скролла
        //
        //scrollIntoView(this.cardView);
    }

    _prepareQuestionModel(cardId) {
        const card = this.cardsEverMeet[cardId];
        const useFace = this.cardConfig.question === 'term';
        const model = {
            id: cardId,
            image: useFace ? card.faceImageSrc : card.backImageSrc,
            text: useFace ? card.faceText : card.backText,
            textType: (useFace ? card.faceT : card.backT) === 1 ? 'html' : 'text',
            audio: useFace ? card.faceA : card.backA,
            lang: useFace ? card.faceL : card.backL
        };
        model.textOnly = cardTextOnly(model.text, model.textType)
        return model;
    }

    _prepareAnswerModel(cardId, shortcut) {
        const card = this.cardsEverMeet[cardId];
        const useFace = this.cardConfig.question === 'term';
        const model = {
            id: cardId,
            image: useFace ? card.backImageSrc : card.faceImageSrc,
            text: (useFace ? card.backText : card.faceText) || "",
            textType: (useFace ? card.backT : card.faceT) === 1 ? 'html' : 'text',
            shortcut: shortcut
        };
        model.textOnly = cardTextOnly(model.text, model.textType);
        return model;
    }

    _saveStatus() {
        if (this.authenticated && !this.reviewMode) {
            this.progress.flushChanges().forEach(cardData => {
                $.ajax({
                    url: `${Flashcards.root}/set/${this.packId}/learn/answer.ajax`,
                    type: 'POST',
                    contentType: 'application/json',
                    data: JSON.stringify(cardData)
                });
            });
        }
    }
}

class PackLearnStrategy {
    /**
     * @param {PackLearn} controller
     */
    constructor(controller) {
        this.controller = controller;
    }

    show() {
    }

    stop() {
        this._unadviseEvents();
        this.controller.userChoiceEnabled = false;
    }

    _unadviseEvents() {
    }

    _scrollToCard() {
        this.controller._scrollToCard();
    }

    _showAnswerCorrection() {
        const l18n = this.controller.l18n;
        const correctAnswerModel = this.controller._prepareAnswerModel(this.controller.card);
        const wrongHtml = WRONG_TEMPLATE.render({
            title: l18n.get('packPlay.wrongDisplay.title'),
            label: l18n.get('packPlay.wrongDisplay.label'),
            continueText: l18n.get('packPlay.wrongDisplay.continueText'),
            answerTextType: correctAnswerModel.textType,
            answerText: correctAnswerModel.text
        });

        this.controller.cardView.empty().append(wrongHtml);
        fitText(this.controller.cardView.find('p[data-fittext], div[data-fittext]'));
    }

    _userAnswerCorrect(mode) {
        setTimeout(() => {
            this._proceedNext(mode);
        }, 500);
    }

    _userAnswerIncorrect(mode) {
        WaitUserAction.wait(() => {
            this._proceedNext(mode);
        });
    }

    _proceedNext(mode) {
        if (this.answerCorrect) {
            this.controller.progress.incrementSuccess(mode, this.controller.card);
        } else {
            this.controller.progress.incrementFailure(mode, this.controller.card);
        }
        this.controller.learnQueue.next(this.answerCorrect);

        this.controller.cardStrategy = null;
        this.controller.updateProgress();
        this.controller._saveStatus();
        this.controller._next(true);
        this._scrollToCard();
    }

    finishAnswer(mode, isCorrect) {
        log(['finish', this, isCorrect]);

        this.answerCorrect = isCorrect;
        if (!isCorrect) {
            this._showAnswerCorrection();
        }

        if (isCorrect) {
            this._userAnswerCorrect(mode);
        } else {
            this._userAnswerIncorrect(mode);
        }
    }
}

const packLearnChoiceAnswerTemplate = $.templates(`
<li>
    <div class="fl-learn-card__answers-item">
        <a class="btn btn-light fl-learn-card__answers-answer {{if image && text}}fl-learn-card__answers-answer--image-and-text{{/if}}" data-card="{{:id}}" data-shortcut="{{:shortcut}}">
            {{if}}<div class="fl-learn-card__answers-answer-shortcut">{{>shortcut}}</div>{{/if}}
            {{if image}}<div class="fl-learn-card__answers-answer-image"><img alt="" src="{{attr:image}}" data-fitimage style="display: none;"></div>{{/if}}
            {{if text}}<div class="fl-learn-card__answers-answer-text"><div class="fl-tt-{{:textType}}" data-fittext>{{if textType == 'html}}{{: text}}{{else}}{{> text}}{{/if}}</div></div>{{/if}}
        </a>
    </div>
</li>
`);

class PackLearnChoice extends PackLearnStrategy {
    constructor(controller) {
        super(controller);

        this.shortcuts = {};
        this.shortcuts['1'] = () => this._userChoiceByShortcut('1');
        this.shortcuts['2'] = () => this._userChoiceByShortcut('2');
        this.shortcuts['3'] = () => this._userChoiceByShortcut('3');
        this.shortcuts['4'] = () => this._userChoiceByShortcut('4');

        this._userChoiceByClickHandler = e => {
            this._userChoiceByClick($(e.currentTarget));
        };
    }

    show() {
        super.show();
        const card = this.controller.card;

        // take some four
        const cardAnswer = this.controller._prepareAnswerModel(card);
        const answers = this.controller.randomCardsWithout(3, card)
            .map(id => this.controller._prepareAnswerModel(id))
            .filter(model => model.text === "" || model.text !== cardAnswer.text);

        answers.push(cardAnswer);
        shuffleArray(answers);

        // assign shortcuts
        answers.forEach((model, index) => model.shortcut = index + 1);

        const answersHtml = answers
            .map(model => packLearnChoiceAnswerTemplate.render(model))
            .join("");

        this.controller.answerList
            .empty()
            .append(answersHtml)
            .show();

        fitText(this.controller.answerList.find('p[data-fittext], div[data-fittext]'));
        this.controller.answerList.find('img[data-fitimage]')
            .fitImage({show: true});

        this._adviseEvents();
    }

    _adviseEvents() {
        log('PackLearnChoice::_adviseEvents');

        this.controller.answerList.one('click', 'a[data-card]', this._userChoiceByClickHandler);

        Object.keys(this.shortcuts).forEach(key => {
            $(document).on('keypress', null, key, this.shortcuts[key]);
        });
    }

    _unadviseEvents() {
        log('PackLearnChoice::_unadviseEvents');

        this.controller.answerList.off('click', 'a[data-card]', this._userChoiceByClickHandler);

        Object.keys(this.shortcuts).forEach(key => {
            $(document).off('keypress', this.shortcuts[key]);
        });
    }

    _userChoiceByClick(button) {
        if (!button.hasClass('disabled')) {
            this._userChoice(button.data('card'));
        }
    }

    _userChoiceByShortcut(shortcut) {
        const button = this.controller.answerList.find(`a[data-shortcut=${shortcut}]`);
        if (button.length) {
            this._userChoiceByClick(button);
        }
    }

    _userChoice(answerId) {
        if (!this.controller.userChoiceEnabled) {
            return;
        }

        this.stop();

        const correctId = this.controller.card;
        const answerList = this.controller.answerList;

        answerList.find('a').each(function () {
            const button = $(this), card = button.data('card');
            if (card === answerId || card === correctId) {
                return;
            }
            button.addClass('fl-learn-card__answers-answer--gone');
        });

        answerList.find(`a[data-card=${correctId}]`).addClass('fl-learn-card__answers-answer--correct');

        const isCorrect = answerId === correctId;
        this.finishAnswer('select', isCorrect);

        if (!isCorrect) {
            answerList.find(`a[data-card=${answerId}]`).addClass('fl-learn-card__answers-answer--incorrect');
        }
    }
}

class PackLearnWrite extends PackLearnStrategy {
    constructor(controller) {
        super(controller);

        this.submitHandler = this.onSubmit.bind(this);
        this.giveupHandler = this.onGiveup.bind(this);
        this.stillCorrectHandler = this.onStillCorrect.bind(this);
        this.pressEnterHandler = (e) => {
            if (isEnterEvent(e)) {
                this.onSubmit();
            }
        };
    }

    show() {
        super.show();
        this._adviseEvents();

        const textInput = this.controller.answerWrite.find('input[type=text]')
            .prop('readonly', false)
            .val('');

        this.controller.answerWrite
            .removeClass('fl-learn-card__write--correct')
            .removeClass('fl-learn-card__write--incorrect')
            .show();

        textInput.focus();
    }

    _adviseEvents() {
        log('PackLearnWrite::_adviseEvents');

        this.controller.answerWrite
            .find('button[data-click=submitAnswer]').one('click', this.submitHandler).end()
            .find('button[data-click=giveupAnswer]').one('click', this.giveupHandler).end();
        this.controller.answerWrite.find('input[type=text]')
            .on('keypress', this.pressEnterHandler);
        this.controller.answerWrite.find('div[data-answer-buttons]').show();

        this.controller.answerWrite.find('div[data-answer-buttons-wrong]').hide();
        this.controller.answerWrite
            .find('button[data-click=stillCorrectAnswer]').off('click').end();
    }

    _unadviseEvents() {
        log('PackLearnWrite::_unadviseEvents');

        this.controller.answerWrite
            .find('button[data-click=submitAnswer]').off('click', this.submitHandler).end()
            .find('button[data-click=giveupAnswer]').off('click', this.giveupHandler).end();
        this.controller.answerWrite.find('input[type=text]')
            .off('keypress', this.pressEnterHandler)
            .blur();
        this.controller.answerWrite.find('div[data-answer-buttons]').hide();
    }

    onSubmit() {
        if (!this.controller.userChoiceEnabled) {
            return;
        }

        this.stop();

        const answer = this.controller.answerWrite.find('input[type=text]').val();
        const expected = this.controller._prepareAnswerModel(this.controller.card, null).textOnly;

        this.finishAnswer('write', checkWrittenAnswer(expected, answer));
    }

    onGiveup() {
        if (!this.controller.userChoiceEnabled) {
            return;
        }

        this._unadviseEvents();
        this.controller.userChoiceEnabled = false;

        this.finishAnswer('write', false);
    }

    onStillCorrect() {
        this.answerCorrect = true;
    }

    finishAnswer(mode, isCorrect) {
        super.finishAnswer(mode, isCorrect);

        this.controller.answerWrite
            .toggleClass('fl-learn-card__write--correct', isCorrect)
            .toggleClass('fl-learn-card__write--incorrect', !isCorrect)
            .find('input[type=text]').prop('readonly', true);

        if (!isCorrect && this.controller.answerWrite.find('input[type=text]').val().length > 0) {
            this.controller.answerWrite
                .find('div[data-answer-buttons-wrong]').show().end()
                .find('button[data-click=stillCorrectAnswer]').one('click', this.stillCorrectHandler).show();
        }
    }
}

global.PackLearn = PackLearn;
